import type { LucideIcon } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { Card } from '../primitives/card';
import { Skeleton } from '../primitives/skeleton';
import { cn } from '../lib/cn';

/** Wraps a row or number in a link; the app passes its router Link */
type LinkWrap = (content: ReactNode, className: string) => ReactNode;

/** Colour of a stat's icon circle and progress bar */
export type StatTone = 'brand' | 'info' | 'success' | 'danger';

// Soft tile that fades from the tone's tint to white, with a white rim, like a small raised button
const toneCircle: Record<StatTone, string> = {
  brand: 'from-primary-soft text-primary-hover',
  info: 'from-info-soft text-info',
  success: 'from-success-soft text-success',
  danger: 'from-danger-soft text-danger',
};
const toneBar: Record<StatTone, string> = {
  brand: 'bg-primary',
  info: 'bg-info',
  success: 'bg-success',
  danger: 'bg-danger',
};

interface StatStripItem {
  label: string;
  value: ReactNode;
  /** One short line under the value */
  hint?: ReactNode;
  icon?: LucideIcon;
  tone?: StatTone;
  /** A thin bar under the number, e.g. accepted out of active */
  progress?: { value: number; max: number };
  link?: LinkWrap;
}

/** A row of number tiles, each with a coloured icon. 2–4 items; two per row on phones. */
export function StatStrip({
  items,
  loading,
  className,
}: {
  items: StatStripItem[];
  loading?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 sm:gap-4',
        items.length >= 4 ? 'xl:grid-cols-4' : items.length === 3 ? 'md:grid-cols-3' : '',
        className,
      )}
    >
      {items.map((item) => {
        const tone = item.tone ?? 'brand';
        const Icon = item.icon;
        const share =
          item.progress && item.progress.max > 0 ? Math.min(item.progress.value / item.progress.max, 1) : 0;
        const content = (
          <>
            <div className="flex items-center gap-3">
              {Icon && (
                <span
                  aria-hidden
                  className={cn(
                    'flex size-12 shrink-0 items-center justify-center rounded-xl border-2 border-surface bg-gradient-to-br to-surface shadow-md',
                    toneCircle[tone],
                  )}
                >
                  <Icon className="size-6" strokeWidth={1.75} />
                </span>
              )}
              <div className="grid min-w-0 gap-0.5">
                <span className="truncate text-[0.8125rem] text-muted-foreground">{item.label}</span>
                {loading ? (
                  <Skeleton className="h-6 w-12" />
                ) : (
                  // The hint sits beside the number, so every tile in the row keeps the same height
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="text-3xl leading-tight font-semibold tabular-nums text-secondary">
                      {item.value}
                    </span>
                    {item.hint && <span className="truncate text-xs text-muted-foreground">{item.hint}</span>}
                  </span>
                )}
              </div>
            </div>
            {/* A hairline along the bottom edge; it adds no height */}
            {item.progress && !loading && (
              <div
                aria-hidden
                className="absolute inset-x-4 bottom-1.5 h-1 overflow-hidden rounded-full bg-muted"
              >
                <div
                  className={cn('h-full rounded-full', toneBar[tone])}
                  style={{ width: `${share * 100}%` }}
                />
              </div>
            )}
          </>
        );
        const className = cn(
          'relative grid content-center rounded-2xl bg-surface px-4 py-4 shadow-sm',
          item.link && 'transition-shadow hover:shadow-md',
        );
        return (
          <Fragment key={item.label}>
            {item.link ? item.link(content, className) : <div className={className}>{content}</div>}
          </Fragment>
        );
      })}
    </div>
  );
}

/** Title row of a list card */
function ListHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 pt-4">
      <h2 className="text-[0.9375rem] font-bold text-secondary">{title}</h2>
      {action}
    </div>
  );
}

export interface TodoItem {
  id: string;
  icon: LucideIcon;
  title: ReactNode;
  detail?: ReactNode;
  /** The button text, e.g. "Compare" */
  actionLabel: string;
  link: LinkWrap;
  /** Brand colour for the icon, for the most urgent ones */
  urgent?: boolean;
  /** A number shown large before the title instead of the icon, e.g. 6 jobs to review */
  count?: number;
}

interface TodoListProps {
  title: string;
  items: TodoItem[];
  loading?: boolean;
  /** Shown when nothing is waiting */
  empty: ReactNode;
  /** E.g. a "View all" link */
  action?: ReactNode;
  className?: string;
}

/** What needs the user now, one soft row per action. Leads the start page. */
export function TodoList({ title, items, loading, empty, action, className }: TodoListProps) {
  return (
    <Card className={cn('grid content-start gap-3 pb-4', className)}>
      <ListHeader title={title} action={action} />
      {loading ? (
        <div className="grid gap-2 px-4">
          <Skeleton className="h-14 rounded-xl" />
          <Skeleton className="h-14 rounded-xl" />
        </div>
      ) : items.length === 0 ? (
        <div className="mx-4 rounded-xl bg-muted px-4 py-5 text-sm text-muted-foreground">{empty}</div>
      ) : (
        <ul className="grid gap-2 px-4">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.id}>
                {item.link(
                  <>
                    {item.count !== undefined ? (
                      <span className="flex shrink-0 items-center gap-3 pl-2">
                        <span aria-hidden className="size-2 rounded-full bg-primary" />
                        <span className="min-w-6 text-2xl leading-none font-medium tabular-nums text-secondary">
                          {item.count}
                        </span>
                      </span>
                    ) : (
                      <span
                        aria-hidden
                        className={cn(
                          'flex size-10 shrink-0 items-center justify-center rounded-xl',
                          item.urgent
                            ? 'bg-primary-soft text-primary-hover'
                            : 'bg-surface text-muted-foreground',
                        )}
                      >
                        <Icon className="size-4" />
                      </span>
                    )}
                    <span className="grid min-w-0 flex-1 gap-0.5">
                      <span
                        className={cn(
                          'text-secondary',
                          item.count !== undefined ? 'text-base' : 'text-sm font-semibold',
                        )}
                      >
                        {item.title}
                      </span>
                      {item.detail && (
                        <span className="truncate text-xs text-muted-foreground">{item.detail}</span>
                      )}
                    </span>
                    <span className="hidden shrink-0 items-center gap-2 rounded-xl border border-border bg-surface px-4 py-2 text-sm font-medium text-secondary shadow-xs transition-colors group-hover:border-primary sm:inline-flex">
                      {item.actionLabel}
                      <ArrowRight className="size-4" aria-hidden />
                    </span>
                  </>,
                  'group flex items-center gap-3 rounded-xl bg-muted p-3 pr-4 transition-colors hover:bg-primary-soft/50',
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export interface ActivityItem {
  id: string;
  title: ReactNode;
  detail?: ReactNode;
  /** "2 h ago" */
  time: string;
  /** Not read yet: a dot before it */
  fresh?: boolean;
  link?: LinkWrap;
}

interface ActivityListProps {
  title: string;
  items: ActivityItem[];
  loading?: boolean;
  empty: ReactNode;
  /** E.g. a "View all" link */
  action?: ReactNode;
  /** Smaller text, each line cut to one line; for the narrow context panel */
  compact?: boolean;
  className?: string;
}

/** What happened lately, newest first, as a timeline. */
export function ActivityList({
  title,
  items,
  loading,
  empty,
  action,
  compact,
  className,
}: ActivityListProps) {
  return (
    <Card className={cn('grid content-start gap-3 pb-4', className)}>
      <ListHeader title={title} action={action} />
      {loading ? (
        <div className="grid gap-3 px-5">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : items.length === 0 ? (
        <div className="mx-4 rounded-xl bg-muted px-4 py-5 text-sm text-muted-foreground">{empty}</div>
      ) : (
        <ol className="grid px-3">
          {items.map((item, index) => {
            const content = (
              <>
                {/* The timeline: a dot per event on a thin line */}
                <span aria-hidden className="relative flex w-3 shrink-0 justify-center self-stretch">
                  {index < items.length - 1 && <span className="absolute inset-y-0 top-4 w-px bg-border" />}
                  <span
                    className={cn(
                      'relative rounded-full ring-4 ring-surface',
                      compact ? 'mt-1 size-2' : 'mt-1.5 size-2.5',
                      item.fresh ? 'bg-primary' : 'bg-input',
                    )}
                  />
                </span>
                <span className={cn('grid min-w-0 flex-1 gap-0.5', compact ? 'pb-2' : 'pb-3')}>
                  <span className={cn('font-medium', compact ? 'truncate text-xs' : 'text-sm')}>
                    {item.title}
                  </span>
                  {item.detail && (
                    <span className="truncate text-xs text-muted-foreground">{item.detail}</span>
                  )}
                </span>
                <span className="shrink-0 pt-0.5 text-[0.6875rem] tabular-nums text-muted-foreground">
                  {item.time}
                </span>
              </>
            );
            const className = cn(
              'flex items-start gap-3 rounded-xl px-2',
              compact ? 'pt-1.5' : 'pt-2',
              item.link && 'transition-colors hover:bg-muted',
            );
            return (
              <li key={item.id}>
                {item.link ? item.link(content, className) : <div className={className}>{content}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

interface ProfileCardProps {
  /** The avatar, e.g. <Avatar size="xl" /> */
  avatar: ReactNode;
  name: string;
  /** Role or company under the name */
  subtitle?: string;
  /** Up to three numbers under the name */
  stats?: { label: string; value: ReactNode }[];
  /** Contact and account lines under the numbers: email, phone, company … */
  details?: { icon: LucideIcon; label: string; value: ReactNode }[];
  /** Under the details, e.g. a link to the profile */
  footer?: ReactNode;
  className?: string;
}

/** The signed-in user at the top of the context panel: avatar in an orange ring, name and a few numbers. */
export function ProfileCard({
  avatar,
  name,
  subtitle,
  stats = [],
  details = [],
  footer,
  className,
}: ProfileCardProps) {
  return (
    <section className={cn('grid justify-items-center gap-3 pt-2 text-center', className)}>
      <div className="rounded-full border-[3px] border-primary border-b-secondary/20 border-l-secondary/20 p-1.5">
        {avatar}
      </div>
      <div className="grid gap-0.5">
        <p className="text-xl font-semibold text-secondary">{name}</p>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {stats.length > 0 && (
        <dl className="grid w-full grid-cols-3 divide-x divide-border rounded-xl bg-muted py-3">
          {stats.map((stat) => (
            <div key={stat.label} className="grid gap-0.5 px-1">
              <dd className="order-1 text-2xl font-semibold tabular-nums text-secondary">{stat.value}</dd>
              <dt className="order-2 truncate text-xs text-muted-foreground">{stat.label}</dt>
            </div>
          ))}
        </dl>
      )}
      {details.length > 0 && (
        <dl className="mt-1 grid w-full gap-3 border-t border-border pt-4 text-left">
          {details.map((detail) => (
            <div key={detail.label} className="flex items-center gap-3">
              <span
                aria-hidden
                className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted text-secondary"
              >
                <detail.icon className="size-4" />
              </span>
              <div className="grid min-w-0">
                <dt className="text-[0.6875rem] text-muted-foreground">{detail.label}</dt>
                <dd className="truncate text-sm font-medium">{detail.value}</dd>
              </div>
            </div>
          ))}
        </dl>
      )}
      {footer}
    </section>
  );
}

interface HighlightCardProps {
  title: string;
  children?: ReactNode;
  /** A button or link, shown at the bottom */
  action?: ReactNode;
  icon?: LucideIcon;
  className?: string;
}

/** One solid navy card per page for the most useful next step (a plan, a call to action). Never more than one. */
export function HighlightCard({ title, children, action, icon: Icon, className }: HighlightCardProps) {
  return (
    <section
      className={cn(
        'relative isolate grid gap-4 overflow-hidden rounded-2xl bg-secondary p-6 text-secondary-foreground shadow-lg',
        className,
      )}
    >
      {/* Navy fading into a warm orange glow in the lower right, with a soft darker circle in the top corner */}
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -right-16 -z-10 size-64 rounded-full bg-primary/55 blur-3xl"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-10 -z-10 size-36 rounded-full bg-secondary-hover/70"
      />
      <div className="flex items-center gap-4">
        {Icon && (
          <span
            aria-hidden
            className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary shadow-md"
          >
            <Icon className="size-6 text-primary-foreground" />
          </span>
        )}
        <h2 className="text-lg leading-snug font-bold">{title}</h2>
      </div>
      {children && <div className="text-sm leading-relaxed text-secondary-foreground/85">{children}</div>}
      {action && <div className="justify-self-start">{action}</div>}
    </section>
  );
}

interface ChartCardProps {
  title: string;
  /** One line under the title, e.g. "Last 6 months" */
  description?: string;
  /** Right of the title, e.g. a total */
  aside?: ReactNode;
  loading?: boolean;
  children: ReactNode;
  className?: string;
}

/** A card holding one chart, with its title and an optional figure on the right. */
export function ChartCard({ title, description, aside, loading, children, className }: ChartCardProps) {
  return (
    <Card className={cn('grid content-start gap-4 p-5', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-0.5">
          <h2 className="text-[0.9375rem] font-bold text-secondary">{title}</h2>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {aside}
      </div>
      {loading ? <Skeleton className="h-40" /> : children}
    </Card>
  );
}
